import { matchPath } from "react-router-dom";
import { resetSidebarReducer } from "../../actions/taskSidebar";
import { label, number, route } from "../../config";
import store from "../../store";
import { splitString } from "../../utils";
import { removeSpecialChars } from "../../utils/common";
import { openSidebar } from "../Tasks/tasks.service";
import { ORGANIZATION_FEATURES_TITLES, FEATURE_DISPLAY_NAMES } from "../Admin/ManageOrganization/OrganizationFeatures/organizationFeatures.constants";

/**
 * Configuration for search icon visibility.
 * Simple paths that should show the search icon.
 */
export const SIMPLE_SEARCH_PATHS = new Set([
  route.PRIVATE_ROUTE.QUEUES.PATH,
  route.PRIVATE_ROUTE.PROJECT.PATH,
  route.PRIVATE_ROUTE.COMMENT.PATH,
  route.PRIVATE_ROUTE.REMINDER.PATH,
  route.PRIVATE_ROUTE.NOTIFICATION.PATH,
  route.PRIVATE_ROUTE.USER_DIRECTORY.PATH,
  route.ADMIN_ROUTE.MANAGE_PROJECTS.PATH,
  route.ADMIN_ROUTE.MANAGE_USERS.PATH,
]);

/**
 * Team / user settings tabs that enable context search (user “teams” tab title is `${operationalTeamName}s`, checked at call site).
 */
export const SETTING_TABS = new Set(["Views", "Workflows", "Members", "Forms", "Automation", "Chips"]);

/**
 * Organization features that should show the search icon.
 */
export const ADMIN_ORG_FEATURES = new Set([
  ORGANIZATION_FEATURES_TITLES.JOB_ROLE,
  ORGANIZATION_FEATURES_TITLES.TAGS,
  ORGANIZATION_FEATURES_TITLES.BUSINESS_UNITS,
  ORGANIZATION_FEATURES_TITLES.COST_CENTER,
  ORGANIZATION_FEATURES_TITLES.DEPARTMENTS,
  ORGANIZATION_FEATURES_TITLES.OFFICE_LOCATION,
  ORGANIZATION_FEATURES_TITLES.USER_GROUPS,
  ORGANIZATION_FEATURES_TITLES.CUSTOM_FIELD_SETTINGS,
  ORGANIZATION_FEATURES_TITLES.API_INTEGRATION,
  ORGANIZATION_FEATURES_TITLES.TIME_SHEETS,
]);

/**
 * logic for active search result on Down Arrow key
 * @param {prevState, results}
 * @returns {Object}
 */
export const handleArrowDown = (prevState, results) => {
  if (prevState?.parentIdx === results.length - number.ONE && prevState?.childIdx === results[prevState?.parentIdx]?.searchResult?.length - number.ONE) return prevState;

  if (prevState?.childIdx < results[prevState?.parentIdx]?.searchResult?.length - number.ONE) {
    return { ...prevState, childIdx: prevState?.childIdx + number.ONE }
  } else {
    return results[prevState?.parentIdx + number.ONE]?.searchResult.length ? { parentIdx: prevState?.parentIdx + number.ONE, childIdx: number.ZERO } : { ...prevState }
  }
}

/**
  *logic for active search result on Up Arrow key
  * @param {prevState, results}
  * @returns {Object}
  */
export const handleArrowUp = (prevState, results) => {
  if (prevState?.parentIdx === number.ZERO && prevState?.childIdx === number.ZERO) return prevState;

  if (prevState?.childIdx > number.ZERO) {
    return { ...prevState, childIdx: prevState?.childIdx - number.ONE }
  } else {
    return { parentIdx: prevState?.parentIdx - number.ONE, childIdx: results[prevState?.parentIdx - number.ONE]?.searchResult?.length - number.ONE }
  }
}


/**
 * Condition for item text/code perfect match
 * @param {Object} item 
 * @param {String} searchText 
 * @returns {Boolean}
 */
export const isPerfectMatch = (item, searchText) => {
  let perfectMatch = true;
  let lowerCaseCode = item.Code?.toLowerCase();
  let itemCode = sanitizeText(item.Code);
  let itemName = sanitizeText(item.Name);
  let tags = sanitizeText(item.tagLabels)
  tags = tags?.replaceAll(" ", ",")
  tags = splitString(tags?.toLowerCase(), ",")

  searchText = sanitizeText(searchText);

  let searchSubStrings = splitString(searchText, " ");
  if(!searchSubStrings) return perfectMatch;
  itemCode = splitString(itemCode, " ");
  itemName = splitString(itemName, " ");

  

  let itemSubStrings = itemCode?.concat(itemName).concat(tags).concat([lowerCaseCode?.replaceAll("-", ""), lowerCaseCode?.replaceAll("-", " "), splitString(lowerCaseCode, "-")[0], splitString(lowerCaseCode, "-")[1], splitString(lowerCaseCode, "-")[0]?.replaceAll(/[^a-zA-Z ]/g, "")])


  for (let str of searchSubStrings) {
    if (!itemSubStrings?.includes(str)) {
      perfectMatch = false;
      break;
    }
  }
  return perfectMatch;

}

/**
 * Convert to lower case and remove special characters from text
 * @param {String} text 
 * @returns {String}
 */
const sanitizeText = (text) => removeSpecialChars(text?.trim()?.toLowerCase());

/**
 * checks for only whitespace in search text
 * @param {String} searchText 
 * @returns {Boolean}
 */
export const checkForWhitespace = (searchText) => !(/^\s+$/g).test(searchText);

/**
 * CMS DOX file id from route params or pathname (OverallSearchPopup renders outside the matched route).
 * @param {string} pathname - Current location pathname
 * @param {Object} [routeParams] - `useParams()` result when available
 * @returns {string|undefined}
 * @author Abhishek Dhillon
 */
export const resolveCmsFileIdFromLocation = (pathname, routeParams = {}) => {
  if (routeParams?.fileId) {
    return routeParams.fileId;
  }
  const match = matchPath(pathname, {
    path: route.PRIVATE_ROUTE.Y_DOC.ROUTER_PATH,
    exact: true,
    strict: false,
  });
  return match?.params?.fileId;
};

/**
  * click action of task search result
  * @param {task}
  * @returns {void}
  */
export const taskClickAction = (task) => {
  const state = store.getState();
  store.dispatch(resetSidebarReducer())
  openSidebar({ id: task.Id, userId: state.auth?.user.id, taskHistoryId: null })
}

/**
 * Gets the button text for the search popup based on the current route and context.
 * Route/tab → button text rules; first match wins. Add new entries here when adding search context.
 * @param {Object} params - Parameters object
 * @param {string} params.pathname - Current pathname
 * @param {string} params.lastSelectedBoard - Last selected board
 * @param {string} params.teamLeftSelectedTab - Team left sidebar selected tab
 * @param {string} params.userLeftSelectedTab - User profile left sidebar selected tab
 * @param {boolean} params.inEditMode - Whether in edit mode
 * @param {string} params.activeOrganizationSidebar - Active organization sidebar feature
 * @param {string} [params.operationalTeamName] - User's operational team name
 * @returns {string} Button text for the search popup
 */
export const getSearchButtonText = ({
  pathname,
  lastSelectedBoard,
  teamLeftSelectedTab,
  userLeftSelectedTab,
  inEditMode,
  activeOrganizationSidebar,
  operationalTeamName,
}) => {
  // Extract common path conditions
  const isUserProfile = pathname?.includes(route.PRIVATE_ROUTE.USER_PROFILE_SETTING.PATH);
  const isTeamSetting = pathname?.includes(route.PRIVATE_ROUTE.TEAM_SETTING.PATH);
  const isManageOrganization = pathname?.includes(route.ADMIN_ROUTE.MANAGE_ORGANIZATION.PATH);

  /**
   * Route/tab → button text rules; first match wins. Add new entries here when adding search context.
   */
  const buttonTextRules = [
    { match: () => pathname === route.ADMIN_ROUTE.MANAGE_USERS.PATH, text: () => label.USERS },
    { match: () => pathname?.includes(route.PRIVATE_ROUTE.CMS.PATH), text: () => label.LIBRARY },
    { match: () => pathname === route.PRIVATE_ROUTE.QUEUES.PATH, text: () => label.SPACE },
    { match: () => pathname?.includes(route.ADMIN_ROUTE.ACTIVITY_LOG.PATH), text: () => label.ACTIVITY_LOGS },
    { match: () => pathname === route.PRIVATE_ROUTE.PROJECT.PATH, text: () => label.TEAM },
    { match: () => pathname === route.PRIVATE_ROUTE.REMINDER.PATH, text: () => label.REMINDERS },
    { match: () => pathname === route.PRIVATE_ROUTE.NOTIFICATION.PATH, text: () => label.NOTIFICATIONS },
    { match: () => pathname === route.PRIVATE_ROUTE.USER_DIRECTORY.PATH, text: () => label.USER },
    { match: () => pathname === route.ADMIN_ROUTE.MANAGE_PROJECTS.PATH, text: () => label.TEAM },
    { match: () => isTeamSetting && teamLeftSelectedTab === "Views", text: () => label.VIEWS },
    { match: () => isTeamSetting && teamLeftSelectedTab === "Forms", text: () => label.FORMS },
    { match: () => isTeamSetting && teamLeftSelectedTab === "Workflows", text: () => label.WORKFLOW },
    { match: () => isTeamSetting && teamLeftSelectedTab === "Members", text: () => label.MEMBERS },
    { match: () => isTeamSetting && teamLeftSelectedTab === "Automation", text: () => label.AUTOMATION },
    { match: () => isTeamSetting && teamLeftSelectedTab === "Chips", text: () => label.CHIPS },
    { match: () => isUserProfile && userLeftSelectedTab === "Views", text: () => label.VIEWS },
    { match: () => isUserProfile && userLeftSelectedTab === "Workflows", text: () => label.WORKFLOW },
    {
      match: () => isUserProfile && userLeftSelectedTab === `${operationalTeamName}s`,
      text: () => operationalTeamName || label.TEAM,
    },
    { match: () => isUserProfile && userLeftSelectedTab === "Forms", text: () => label.FORMS },
    { match: () => isUserProfile && userLeftSelectedTab === "Automation", text: () => label.AUTOMATION },
    { match: () => isUserProfile && userLeftSelectedTab === "Chips", text: () => label.CHIPS },
    { match: () => pathname === route.PRIVATE_ROUTE.COMMENT.PATH, text: () => label.COMMENTS },
    { match: () => pathname?.includes(route.PRIVATE_ROUTE.AUTOMATION_DETAIL.PATH), text: () => label.AUTOMATIONS },
    { match: () => lastSelectedBoard === label.STREAM && !inEditMode, text: () => label.STREAM },
    {
      match: () => isManageOrganization && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.JOB_ROLE,
      text: () => label.JOB_ROLES,
    },
    {
      match: () => isManageOrganization && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.TAGS,
      text: () => label.TAGS,
    },
    {
      match: () => isManageOrganization && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.BUSINESS_UNITS,
      text: () => FEATURE_DISPLAY_NAMES[ORGANIZATION_FEATURES_TITLES.BUSINESS_UNITS] || ORGANIZATION_FEATURES_TITLES.BUSINESS_UNITS,
    },
    {
      match: () => isManageOrganization && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.COST_CENTER,
      text: () => FEATURE_DISPLAY_NAMES[ORGANIZATION_FEATURES_TITLES.COST_CENTER] || ORGANIZATION_FEATURES_TITLES.COST_CENTER,
    },
    {
      match: () => isManageOrganization && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.DEPARTMENTS,
      text: () => FEATURE_DISPLAY_NAMES[ORGANIZATION_FEATURES_TITLES.DEPARTMENTS] || ORGANIZATION_FEATURES_TITLES.DEPARTMENTS,
    },
    {
      match: () => isManageOrganization && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.OFFICE_LOCATION,
      text: () => FEATURE_DISPLAY_NAMES[ORGANIZATION_FEATURES_TITLES.OFFICE_LOCATION] || ORGANIZATION_FEATURES_TITLES.OFFICE_LOCATION,
    },
    {
      match: () => isManageOrganization && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.USER_GROUPS,
      text: () => label.USER_GROUPS || ORGANIZATION_FEATURES_TITLES.USER_GROUPS,
    },
    {
      match: () => isManageOrganization && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.CUSTOM_FIELD_SETTINGS,
      text: () => FEATURE_DISPLAY_NAMES[ORGANIZATION_FEATURES_TITLES.CUSTOM_FIELD_SETTINGS] || ORGANIZATION_FEATURES_TITLES.CUSTOM_FIELD_SETTINGS,
    },
    {
      match: () => isManageOrganization && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.API_INTEGRATION,
      text: () => label.API,
    },
    {
      match: () => isManageOrganization && activeOrganizationSidebar === ORGANIZATION_FEATURES_TITLES.TIME_SHEETS,
      text: () => FEATURE_DISPLAY_NAMES[ORGANIZATION_FEATURES_TITLES.TIME_SHEETS] || ORGANIZATION_FEATURES_TITLES.TIME_SHEETS,
    },
  ];

  const matchedRule = buttonTextRules.find((rule) => rule.match());
  return matchedRule ? matchedRule.text() : label.COMMENTS;
};